import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
    fetchReleases,
    clearCache,
} from "../services/backendApi";
import {
    getNewReturningBreakdown,
    getGenreBuckets,
    getNetworkBuckets,
    getRatingSummary,
    getSourceBreakdown,
    getRatingDistributionBuckets,
    getDailyReleaseVolume,
    getTopRatedUpcoming,
    calculateTrend,
} from "../utils/insights";

export const useReleases = (forceRefresh = false, dateRange = "next", initialPayload = null) => {
    const hasInitialPayload = Boolean(initialPayload && Array.isArray(initialPayload.data));
    const [releases, setReleases] = useState(() => (
        hasInitialPayload ? initialPayload.data : []
    ));
    const [loading, setLoading] = useState(() => !hasInitialPayload);
    const [error, setError] = useState(null);
    const [dateRangeLoading, setDateRangeLoading] = useState(false);
    const [metadata, setMetadata] = useState(() => {
        if (!hasInitialPayload) {
            return {
                referenceDay: null,
                lastUpdated: null,
                count: 0,
                totalCount: 0,
                candidateCount: null,
                stats: null,
                previousStats: null,
                previousLastUpdated: null,
                previousTotalCount: null,
                monthIndex: null,
                ifYouLikedPairings: [],
            };
        }
        return {
            referenceDay:
                typeof initialPayload.referenceDay === "string"
                    ? initialPayload.referenceDay
                    : null,
            lastUpdated: initialPayload.lastUpdated || null,
            count:
                typeof initialPayload.count === "number"
                    ? initialPayload.count
                    : initialPayload.data.length,
            totalCount:
                typeof initialPayload.totalCount === "number"
                    ? initialPayload.totalCount
                    : initialPayload.data.length,
            candidateCount:
                typeof initialPayload.candidateCount === "number"
                    ? initialPayload.candidateCount
                    : null,
            stats: initialPayload.stats || null,
            previousStats: initialPayload.previousStats || null,
            previousLastUpdated: initialPayload.previousLastUpdated || null,
            previousTotalCount:
                typeof initialPayload.previousTotalCount === "number"
                    ? initialPayload.previousTotalCount
                    : null,
            monthIndex: initialPayload.monthIndex || null,
            ifYouLikedPairings: Array.isArray(initialPayload.ifYouLikedPairings)
                ? initialPayload.ifYouLikedPairings
                : [],
        };
    });
    const prevDateRangeRef = useRef(dateRange);
    const hasUsedInitialPayloadRef = useRef(hasInitialPayload);
    const requestIdRef = useRef(0);

    const fetchData = useCallback(
        async (refresh = false) => {
            const requestId = ++requestIdRef.current;
            try {
                // Check if this is a date range change
                const isDateRangeChange = prevDateRangeRef.current !== dateRange;
                if (isDateRangeChange) {
                    setDateRangeLoading(true);
                }
                prevDateRangeRef.current = dateRange;

                if (refresh) {
                    clearCache();
                }

                // Backend handles ALL filtering - we just pass the data through
                const payload = await fetchReleases({ dateRange });
                if (requestId !== requestIdRef.current) {
                    return;
                }

                const nextReleases = Array.isArray(payload.data) ? payload.data : [];
                setReleases(nextReleases);
                setMetadata({
                    referenceDay:
                        typeof payload.referenceDay === "string"
                            ? payload.referenceDay
                            : null,
                    lastUpdated: payload.lastUpdated || null,
                    count:
                        typeof payload.count === "number"
                            ? payload.count
                            : nextReleases.length,
                    totalCount:
                        typeof payload.totalCount === "number"
                            ? payload.totalCount
                            : nextReleases.length,
                    candidateCount:
                        typeof payload.candidateCount === "number"
                            ? payload.candidateCount
                            : null,
                    stats: payload.stats || null,
                    previousStats: payload.previousStats || null,
                    previousLastUpdated: payload.previousLastUpdated || null,
                    previousTotalCount:
                        typeof payload.previousTotalCount === "number"
                            ? payload.previousTotalCount
                            : null,
                    monthIndex: payload.monthIndex || null,
                    ifYouLikedPairings: Array.isArray(payload.ifYouLikedPairings)
                        ? payload.ifYouLikedPairings
                        : [],
                });
                setError(null);

            } catch (err) {
                if (requestId !== requestIdRef.current) {
                    return;
                }
                console.error("Error fetching releases:", err);
                setError(err.message);
            } finally {
                if (requestId === requestIdRef.current) {
                    setLoading(false);
                    setDateRangeLoading(false);
                }
            }
        },
        [dateRange]
    );

    useEffect(() => {
        if (hasUsedInitialPayloadRef.current && !forceRefresh && dateRange === "next") {
            hasUsedInitialPayloadRef.current = false;
            return;
        }
        fetchData(forceRefresh);
    }, [fetchData, forceRefresh, dateRange]);

    const refreshReleases = useCallback(() => {
        setLoading(true);
        return fetchData(true);
    }, [fetchData]);

    const insights = useMemo(() => {
        const current = Array.isArray(releases) ? releases : [];
        const previousTotalCount =
            metadata.previousLastUpdated && typeof metadata.previousTotalCount === "number"
                ? metadata.previousTotalCount
                : null;
        const today = new Date();
        const windowStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const windowEnd = new Date(windowStart);
        if (dateRange === "previous") {
            windowStart.setDate(windowStart.getDate() - 30);
        } else {
            windowEnd.setDate(windowEnd.getDate() + 30);
        }
        const totalTrend = calculateTrend(
            metadata.totalCount ?? current.length,
            previousTotalCount
        );
        const premiumTrend = calculateTrend(
            metadata.count ?? current.length,
            previousTotalCount
        );
        const ratingSummary = getRatingSummary(current);
        return {
            newVsReturning: getNewReturningBreakdown(current),
            genreBuckets: getGenreBuckets(current),
            networkBuckets: getNetworkBuckets(current),
            ratingSummary,
            sourceBreakdown: getSourceBreakdown(current),
            ratingBuckets: getRatingDistributionBuckets(ratingSummary),
            dailyVolume: getDailyReleaseVolume(current, { startDate: windowStart, endDate: windowEnd }),
            topRatedUpcoming: getTopRatedUpcoming(current, 5),
            trends: {
                totalCount: totalTrend,
                premiumCount: premiumTrend,
            },
        };
    }, [
        releases,
        metadata.totalCount,
        metadata.previousTotalCount,
        metadata.previousLastUpdated,
        metadata.count,
        dateRange,
    ]);

    return {
        releases,
        loading,
        error,
        dateRangeLoading,
        refreshReleases,
        referenceDay: metadata.referenceDay,
        lastUpdated: metadata.lastUpdated,
        count: metadata.count,
        totalCount: metadata.totalCount,
        candidateCount: metadata.candidateCount,
        stats: metadata.stats,
        previousStats: metadata.previousStats,
        previousLastUpdated: metadata.previousLastUpdated,
        previousTotalCount: metadata.previousTotalCount,
        monthIndex: metadata.monthIndex,
        ifYouLikedPairings: metadata.ifYouLikedPairings || [],
        insights,
    };
};
